import { IModalEntity } from '../../../../../interfaces/modalEntity';
import { IExperience } from '../../../../../interfaces/experience';
import { IEducation } from '../../../../../interfaces/education';
import { useSearchParams } from 'react-router-dom';
import { Modal } from '../../../../common/Modal';
import {
  ChangeEvent, Dispatch, SetStateAction,
  SyntheticEvent,
  useCallback, useContext,
  useMemo,
  useState
} from 'react';
import { Checkbox, FormControlLabel, FormGroup, Icon, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ButtonWithLoader } from '../../../../common/ButtonWithLoader';
import { TEXTS } from '../../../../../core/texts';
import { ColumnBoxFlexFullWidth } from '../../../../common/boxes';
import Dates from './Dates';
import { Validator } from '../../../../../core/Validator';
import { ValidationErrorState } from '../../../../../types/validationErrorState';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerContext } from '../../FreelancerEntity';
import { FreelancerService } from '../../../../../services/freelancer';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../../interfaces/errorResponse';
import SelectCountryAndTown from '../../../../common/SelectCountryAndTown';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../../queryHooks/freelancer/useGetFreelancerEntity';
import { useNotification } from '../../../../../+state/useNotification';
import { BaseObj } from '../../../../../types/baseObj';
import { ILocation } from '../../../../../interfaces/location';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const generateInitialState = (entity: IExperience | IEducation | undefined, isExperienceType: boolean): IExperience | IEducation => {
  const commonState: Partial<IExperience> | Partial<IEducation> = {
    id: entity?.id,
    description: entity?.description || '',
    startDateMonth: entity?.startDateMonth,
    startDateYear: entity?.startDateYear,
    endDateMonth: entity ? entity.endDateMonth : currentMonth,
    endDateYear: entity ? entity.endDateYear : currentYear,
  };
  if (isExperienceType) {
    const expEntity = entity as IExperience | undefined;
    return {
      ...commonState,
      company: expEntity?.company || '',
      role: expEntity?.role || '',
    } as IExperience;
  }
  const educationEntity = entity as IEducation | undefined;
  return {
    ...commonState,
    name: educationEntity?.name || '',
    institution: educationEntity?.institution || '',
    country: educationEntity?.country || '',
    town: educationEntity?.town || ''
  } as IEducation;
};

const ExperienceOrEducationEntityModal = ({ entity, onClose }: IModalEntity<IExperience | IEducation>) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { user } = useContext(FreelancerContext);
  const isTypeExperience = useMemo(() => !!searchParams.get('experience'), [searchParams]);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();

  const onSuccessHandler = useCallback(() => {
    openSuccessToast('Successfully saved');
    onClose();
    queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
  }, [onClose, openSuccessToast, queryClient]);

  const onFailHandler = useCallback((err: AxiosError<IErrorResponse>) => {
    openErrorToast(err.response?.data.message || err.message);
  }, [openErrorToast]);

  const mutationExperience = useMutation({
    mutationFn: (exp: IExperience) => exp.id
      ? FreelancerService.editExperience(user.id, exp as IExperience & { id: string })
      : FreelancerService.addExperience(user.id, exp),
    onSuccess: () => onSuccessHandler(),
    onError: (err: AxiosError<IErrorResponse>) => onFailHandler(err)
  });

  const mutationEducation = useMutation({
    mutationFn: (education: IEducation) => education.id
      ? FreelancerService.editEducation(user.id, education as IEducation & { id: string })
      : FreelancerService.addEducation(user.id, education),
    onSuccess: () => onSuccessHandler(),
    onError: (err: AxiosError<IErrorResponse>) => onFailHandler(err)
  });
  const isLoading = useMemo(() => mutationExperience.isLoading || mutationEducation.isLoading,
    [mutationEducation.isLoading, mutationExperience.isLoading]);

  const [state, setState] = useState(generateInitialState(entity, isTypeExperience));
  const [validationError, setValidationError] = useState<ValidationErrorState>({});
  const constraints = useMemo<any>(() => isTypeExperience ? Validator.LENGTHS.EXPERIENCE : Validator.LENGTHS.EDUCATION, [isTypeExperience]);

  const hasChanges = useMemo(() => {
    return !entity || !!Object.keys(state).find(k => (state as any)[k] !== (entity as any)[k]);
  }, [entity, state]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof typeof state;
    setState(prev => ({ ...prev, [name]: e.target.value }));
  }, []);

  const handleCheckUntilNow = useCallback((e: SyntheticEvent) => {
    if ((e.target as any).checked) {
      return setState(prev => ({ ...prev, endDateYear: null, endDateMonth: null }));
    }
    setState(prev => ({
      ...prev,
      endDateYear: entity?.endDateYear || currentYear,
      endDateMonth: entity?.endDateMonth || currentMonth
    }));
  }, [entity]);

  const handleSubmit = useCallback(() => {
    const errors = Validator.validate(state as any, isTypeExperience ? Validator.NAMES.EXPERIENCE : Validator.NAMES.EDUCATION);
    if (errors) {
      return setValidationError(errors);
    }
    const handler = isTypeExperience ? mutationExperience.mutate : mutationEducation.mutate;
    (handler as any)(state);
  }, [mutationExperience, mutationEducation, isTypeExperience, state]);

  return (
    <Modal onClose={onClose}
           maxWidth={800}
           title={`${entity ? 'Edit' : 'New'} ${isTypeExperience ? 'experience' : 'education'}`}
           headerIcon={<Icon
             sx={{ color: theme.palette.primary.main }}>{isTypeExperience ? 'work-history' : 'school'}</Icon>}
           dialogActions={
             <ButtonWithLoader
               onClick={handleSubmit}
               sx={{ my: 2, fontSize: { mobile: 18, tablet: 24 } }}
               disabled={!hasChanges}
               loading={isLoading}>
               {TEXTS.CONFIRM}
             </ButtonWithLoader>
           }
    >
      <ColumnBoxFlexFullWidth>

        {
          isTypeExperience
            ? <ColumnBoxFlexFullWidth>
              <TextField variant={'standard'} label={TEXTS.COMPANY} name={'company'}
                         value={(state as IExperience).company}
                         onChange={handleInputChange} sx={{ mb: 2 }}
                         error={!!validationError['company']}
                         helperText={validationError['company']}
                         required={!!constraints['company']?.min}
              />

              <TextField variant={'standard'} label={TEXTS.ROLE} name={'role'} value={(state as IExperience).role}
                         onChange={handleInputChange} sx={{ mb: 2 }}
                         error={!!validationError['role']}
                         helperText={validationError['role']}
                         required={!!constraints['role']?.min}
              />
            </ColumnBoxFlexFullWidth>

            :
            <ColumnBoxFlexFullWidth mb={2}>
              <TextField variant={'standard'} label={TEXTS.NAME} name={'name'} value={(state as IEducation).name}
                         onChange={handleInputChange}
                         sx={{ mb: 2 }} error={!!validationError.name} helperText={validationError.name}
                         required={!!constraints['name']?.min}
              />

              <TextField variant={'standard'} label={TEXTS.INSTITUTION} name={'institution'}
                         value={(state as IEducation).institution}
                         onChange={handleInputChange}
                         sx={{ mb: 2 }} error={!!validationError.institution} helperText={validationError.institution}
                         required={!!constraints['institution']?.min}
              />
              <SelectCountryAndTown
                variant={'standard'}
                validationErrors={validationError}
                setState={setState as Dispatch<SetStateAction<BaseObj & ILocation>>}
                countryValue={(state as IEducation).country}
                townValue={(state as IEducation).town}
              />
            </ColumnBoxFlexFullWidth>
        }

        <TextField variant={'standard'} label={TEXTS.DESCRIPTION} name={'description'} value={state.description}
                   onChange={handleInputChange} sx={{ mb: 2 }} error={!!validationError.description}
                   helperText={validationError.description} required={!!constraints['description']?.min}
        />

        <Dates state={state} setState={setState} validationError={validationError}/>
        {
          (!entity || (!!entity && !entity.endDateYear)) &&
          <FormGroup sx={{ mt: 2, display: 'inline-block' }}>
            <FormControlLabel control={<Checkbox checked={!state.endDateYear}/>}
                              label={!isTypeExperience ? `I'm still studying` : `I'm still working at this position`}
                              onChange={handleCheckUntilNow}
            />
          </FormGroup>
        }
        
      </ColumnBoxFlexFullWidth>
    </Modal>
  );
};

export default ExperienceOrEducationEntityModal;